import { useState } from 'react';
import { ArrowDownCircle, ArrowUpCircle, FileText, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { toast } from '@/stores/toast-store';
import { useRPNachFileRecords, type RPNachFileRecord } from '@/hooks/use-receive-payments';
import { useSPNachFileRecords, type SPNachFileRecord } from '@/hooks/use-send-payments';
import { formatDateTime } from '@/lib/utils';

// ── Batch Cards (from develop) ────────────────────────────────────────────────

function BatchCard({
  icon: Icon,
  title,
  onInitialize,
  loading,
}: {
  icon: React.ElementType;
  title: string;
  onInitialize: () => void;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      </div>
      <button
        type="button"
        onClick={onInitialize}
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Initializing...' : 'Initialize Process'}
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: string | null | undefined): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function maskAccount(value: string | null | undefined): string {
  if (!value) return '—';
  return `••••${value.slice(-4)}`;
}

function indicatorLabel(indicator: number): string {
  if (indicator === 1) return 'File Header';
  if (indicator === 5) return 'Batch Header';
  if (indicator === 6) return 'Transaction';
  if (indicator === 8) return 'Batch Control';
  if (indicator === 9) return 'File Control';
  return `Indicator ${indicator}`;
}

// ── NachRecordCards ───────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-2 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-foreground text-right break-all">{value}</span>
    </div>
  );
}

function RPNachRecordCard({ item }: { item: RPNachFileRecord }) {
  const { record, fileSequence } = item;
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground truncate">
          {record.customerName || 'Unknown Customer'}
        </span>
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          {indicatorLabel(record.indicator)}
        </span>
      </div>
      <div className="space-y-2">
        <DetailRow label="File Sequence" value={fileSequence || '—'} />
        <DetailRow label="Sequence Index" value={record.sequenceIndex} />
        {record.transactionAmount && <DetailRow label="Amount" value={formatCurrency(record.transactionAmount)} />}
        {record.routingNumber && <DetailRow label="Routing #" value={record.routingNumber} />}
        {record.accountNumber && <DetailRow label="Account #" value={maskAccount(record.accountNumber)} />}
        {record.batchNumber !== null && record.batchNumber !== undefined && (
          <DetailRow label="Batch #" value={String(record.batchNumber)} />
        )}
        {record.addenda && <DetailRow label="Addenda" value={record.addenda} />}
        <DetailRow label="Created" value={formatDateTime(record.createdAt)} />
      </div>
    </div>
  );
}

function SPNachRecordCard({ item }: { item: SPNachFileRecord }) {
  const { record, fileSequence, startDate, endDate } = item;
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground truncate">{record.payeeName || 'Unknown Payee'}</span>
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          {indicatorLabel(record.indicator)}
        </span>
      </div>
      <div className="space-y-2">
        <DetailRow label="File Sequence" value={fileSequence || '—'} />
        <DetailRow label="Sequence Index" value={record.sequenceIndex} />
        {record.transactionAmount && <DetailRow label="Amount" value={formatCurrency(record.transactionAmount)} />}
        {record.routingNumber && <DetailRow label="Routing #" value={record.routingNumber} />}
        {record.accountNumber && <DetailRow label="Account #" value={maskAccount(record.accountNumber)} />}
        {record.batchNumber !== null && record.batchNumber !== undefined && (
          <DetailRow label="Batch #" value={String(record.batchNumber)} />
        )}
        {startDate && endDate && <DetailRow label="Period" value={`${startDate} → ${endDate}`} />}
        {record.addenda && <DetailRow label="Addenda" value={record.addenda} />}
        <DetailRow label="Created" value={formatDateTime(record.createdAt)} />
      </div>
    </div>
  );
}

// ── Tab content ───────────────────────────────────────────────────────────────

function ReceivePaymentsTab() {
  const { data, isLoading, isError } = useRPNachFileRecords({ limit: 50 });
  const records = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-sm">Loading records…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-destructive/30 text-destructive">
        <FileText className="mb-2 h-8 w-8 opacity-50" />
        <p className="text-sm font-medium">Failed to load records</p>
        <p className="mt-1 text-xs text-muted-foreground">Please try again later</p>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground">
        <FileText className="mb-2 h-8 w-8 opacity-40" />
        <p className="text-sm font-medium">No records found</p>
        <p className="mt-1 text-xs">No NACHA file records have been generated yet</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {records.map((item) => (
        <RPNachRecordCard key={item.record.id} item={item} />
      ))}
    </div>
  );
}

function SendPaymentsTab() {
  const { data, isLoading, isError } = useSPNachFileRecords({ limit: 50 });
  const records = data?.data ?? [];

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-sm">Loading records…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-destructive/30 text-destructive">
        <FileText className="mb-2 h-8 w-8 opacity-50" />
        <p className="text-sm font-medium">Failed to load records</p>
        <p className="mt-1 text-xs text-muted-foreground">Please try again later</p>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground">
        <FileText className="mb-2 h-8 w-8 opacity-40" />
        <p className="text-sm font-medium">No records found</p>
        <p className="mt-1 text-xs">No NACHA file records have been generated yet</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {records.map((item) => (
        <SPNachRecordCard key={item.record.id} item={item} />
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type ActiveTab = 'receive' | 'send';

export function AdminBatchProcessPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('receive');
  const [rpLoading, setRpLoading] = useState(false);
  const [spLoading, setSpLoading] = useState(false);

  async function handleReceivePayments() {
    setRpLoading(true);
    try {
      const response = await fetch('/rp-api/v1/nach/download', { credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        toast.error(err?.error?.message || 'Failed to initialize Receive Payments Batch Process');
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? 'rp_nach_batch.csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Initialised the Receive Payments Batch Process');
    } catch {
      toast.error('Failed to initialize Receive Payments Batch Process');
    } finally {
      setRpLoading(false);
    }
  }

  async function handleSendPayments() {
    setSpLoading(true);
    try {
      const response = await fetch('/sp-api/v1/nach/download', { credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        toast.error(err?.error?.message || 'Failed to initialize Send Payments Batch Process');
        return;
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? 'sp_nach_batch.csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Initialised the Send Payments Batch Process');
    } catch {
      toast.error('Failed to initialize Send Payments Batch Process');
    } finally {
      setSpLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Receive / Send Payments Batch Process"
        description="Manage and initialize batch processes for receiving and sending payments."
      />

      <div className="grid gap-6 sm:grid-cols-2">
        <BatchCard
          icon={ArrowDownCircle}
          title="Receive Payments Batch"
          onInitialize={handleReceivePayments}
          loading={rpLoading}
        />
        <BatchCard
          icon={ArrowUpCircle}
          title="Send Payments Batch"
          onInitialize={handleSendPayments}
          loading={spLoading}
        />
      </div>

      {/* NACHA Records Section */}
      <div className="rounded-xl border border-border bg-white shadow-sm">
        <div className="flex border-b border-border px-6">
          {(
            [
              { id: 'receive', label: 'Receive Payments' },
              { id: 'send', label: 'Send Payments' },
            ] as { id: ActiveTab; label: string }[]
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={[
                'relative py-4 px-1 mr-6 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'receive' && <ReceivePaymentsTab />}
          {activeTab === 'send' && <SendPaymentsTab />}
        </div>
      </div>
    </div>
  );
}
